import React, { useState, useEffect, Dispatch, SetStateAction } from "react";
import { CheckBox, CheckBoxVariant, TextButton } from "../../controls";
import "./SeemAI.scss";
import AILabel from "./AILabel";
import { DateTime } from "luxon";
import { useInterval } from "react-use";
import UserFeedbackButtons from "./UserFeedbackButtons";

export interface SeemAIProps {
  onGenerateAI?: () => void;
  loading?: boolean;
  isGeneratedByAi?: boolean;
  error?: string;
  previewMode?: boolean;
  setPreviewMode?: Dispatch<SetStateAction<boolean>>;
  aiText?: string;
  generatedByAiText?: string;
  hideGenerateBtn?: boolean;
  displayLikeBtn?: boolean;
  lastGeneratedTimeString?: string;
  generatedByAiTextWithWarning?: boolean;
  encourageClick?: boolean;
  regenerateBtnText?: string;
  showBetaLabel?: boolean;
}

const SeemAI: React.FC<SeemAIProps> = ({
  onGenerateAI,
  loading,
  isGeneratedByAi,
  error,
  previewMode,
  setPreviewMode,
  aiText,
  generatedByAiText,
  hideGenerateBtn = false,
  displayLikeBtn = true,
  lastGeneratedTimeString,
  generatedByAiTextWithWarning = false,
  encourageClick = false,
  regenerateBtnText = "Regenerate",
  showBetaLabel = false,
}) => {
  const [timeFromGenerated, setTimeFromGenerated] = useState("");

  useInterval(() => {
    if (!!lastGeneratedTimeString) {
      setTimeFromGenerated(
        DateTime.fromISO(lastGeneratedTimeString).toRelative() ?? ""
      );
    }
  }, 60000);

  useEffect(() => {
    if (!lastGeneratedTimeString) {
      setTimeFromGenerated("");
    }
  }, [lastGeneratedTimeString]);

  return (
    <div className={`seem-ai-generated-row`}>
      <AILabel
        isGeneratedByAi={isGeneratedByAi}
        loading={loading}
        aiText={aiText}
        error={error}
        generatedByAiText={generatedByAiText}
        generatedByAiTextWithWarning={generatedByAiTextWithWarning}
        showBetaLabel={showBetaLabel}
      />

      {!!setPreviewMode && (
        <CheckBox
          data-testid={"toggle-ai-preview"}
          wrapperClassName={
            "seem-ai-generated-row-preview-toggle no-padding-bottom"
          }
          checked={previewMode}
          onChange={() => setPreviewMode((prev) => !prev)}
          label={"Preview"}
          variant={CheckBoxVariant.TOGGLE}
          noMargin
        />
      )}

      <div className={`seem-ai-generated-row-actions`}>
        {!!timeFromGenerated && (
          <span className="seem-ai-generated-row-timestamp">
            Generated {timeFromGenerated}
          </span>
        )}
        {!hideGenerateBtn && (
          <TextButton
            buttonStyle={"ai"}
            label={
              loading
                ? "Generating..."
                : isGeneratedByAi
                  ? regenerateBtnText
                  : "Generate"
            }
            data-testid={"generate-ai"}
            type={`button`}
            onClick={(e) => {
              e!.stopPropagation();
              onGenerateAI?.();
            }}
            wrapperClassName={`${loading ? "is-generating" : ""} ${isGeneratedByAi ? "ai-generated" : ""}`}
            className={`generate-ai-button ${encourageClick ? "encourage-click" : ""}`}
            disabled={loading}
          />
        )}
        {isGeneratedByAi && displayLikeBtn && !loading && (
          <UserFeedbackButtons />
        )}
      </div>
    </div>
  );
};

export default SeemAI;
